import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Activity, Users, ShoppingBag, Tv2, Inbox, BadgeCheck, LogOut, Search, Check, X, ChevronDown } from "lucide-react";

function fmt(n) { return typeof n === "number" ? n.toLocaleString() : "—"; }
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

function Stat({ icon: Icon, label, value, sub, color = "#FF4D6D" }) {
  return (
    <div className="border bg-white p-5 flex flex-col gap-3 rounded-none" style={{ borderColor: "rgba(27,19,64,0.08)" }} data-testid={`admin-stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#5E6660]">{label}</span>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <div className="font-display font-medium text-3xl tracking-tight" style={{ color: "#1B1340" }}>{fmt(value)}</div>
      {sub && <div className="text-xs text-[#5E6660]">{sub}</div>}
    </div>
  );
}

const PLAN_LABELS = {
  monthly_10: "€10/mo",
  annual_100: "€100/yr",
  comp: "Comp",
};

function BrandsPanel() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all"); // all | paid | sandbox
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (status !== "all") params.status = status;
      const r = await api.get("/admin/users", { params });
      setRows(r.data || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [q, status]);

  useEffect(() => {
    const t = setTimeout(load, 250); // debounce search
    return () => clearTimeout(t);
  }, [load]);

  const patch = async (id, body) => {
    setBusyId(id); setOpenMenuId(null);
    try {
      const r = await api.patch(`/admin/users/${id}`, body);
      setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...r.data } : row)));
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to update brand");
    } finally {
      setBusyId(null);
    }
  };

  const paidCount = useMemo(() => rows.filter((r) => r.is_paid).length, [rows]);

  return (
    <section className="mt-12" data-testid="admin-brands-panel">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display font-medium text-xl">Brand management</h2>
          <p className="text-sm text-[#5E6660] mt-1">
            Flip <span className="font-mono text-[11px]">is_paid</span> for a brand after an offline payment.
            Paid brands can publish live kiosk links; sandbox brands can only share preview links.
          </p>
        </div>
        <div className="text-xs text-[#5E6660] font-mono uppercase tracking-[0.18em]">
          {loading ? "Loading…" : `${rows.length} brands · ${paidCount} paid`}
        </div>
      </div>

      {/* Search + filter */}
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[260px] max-w-[520px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5E6660]" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by email or brand name…"
            className="w-full h-10 pl-9 pr-3 border bg-white text-sm rounded-none outline-none focus:border-[#1B1340]"
            style={{ borderColor: "rgba(27,19,64,0.15)" }}
            data-testid="admin-brands-search"
          />
        </div>
        <div className="flex border rounded-none overflow-hidden" style={{ borderColor: "rgba(27,19,64,0.15)" }}>
          {["all", "paid", "sandbox"].map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`h-10 px-3 text-xs uppercase tracking-[0.18em] font-mono ${status === s ? "bg-[#1B1340] text-white" : "bg-white text-[#5E6660] hover:text-[#1B1340]"}`}
              data-testid={`admin-brands-filter-${s}`}
            >{s}</button>
          ))}
        </div>
      </div>

      {err && <div className="mt-4 text-sm text-[#FF4D6D]">{err}</div>}

      {/* Table */}
      <div className="mt-4 border bg-white rounded-none overflow-visible" style={{ borderColor: "rgba(27,19,64,0.08)" }}>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[#5E6660] font-mono text-[10px] uppercase tracking-[0.18em] border-b" style={{ borderColor: "rgba(27,19,64,0.08)" }}>
              <th className="py-3 px-4">Brand</th>
              <th className="py-3 px-4">Email</th>
              <th className="py-3 px-4">Status</th>
              <th className="py-3 px-4">Plan</th>
              <th className="py-3 px-4">Joined</th>
              <th className="py-3 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td className="py-6 px-4 text-[#5E6660]" colSpan={6}>No brands match.</td></tr>
            )}
            {rows.map((s) => (
              <tr key={s.id} className="border-b last:border-b-0 relative" style={{ borderColor: "rgba(27,19,64,0.06)" }} data-testid={`admin-brand-row-${s.id}`}>
                <td className="py-3 px-4 font-medium">{s.brand_name || "—"}</td>
                <td className="py-3 px-4 text-[#3F2D5C]">{s.email}</td>
                <td className="py-3 px-4">
                  {s.is_paid ? (
                    <span className="px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase bg-[#14F195]/20 text-[#0a7d4a]" data-testid={`admin-brand-status-${s.id}`}>Paid</span>
                  ) : (
                    <span className="px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase bg-[#FFC233]/20 text-[#7a5300]" data-testid={`admin-brand-status-${s.id}`}>Sandbox</span>
                  )}
                </td>
                <td className="py-3 px-4 text-[#5E6660]">{PLAN_LABELS[s.plan] || "—"}</td>
                <td className="py-3 px-4 text-[#5E6660] whitespace-nowrap">{fmtDate(s.created_at)}</td>
                <td className="py-3 px-4 text-right">
                  {s.is_paid ? (
                    <button
                      onClick={() => patch(s.id, { is_paid: false })}
                      disabled={busyId === s.id}
                      className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-mono text-[#5E6660] hover:text-[#FF4D6D] disabled:opacity-40"
                      data-testid={`admin-brand-revert-${s.id}`}
                    >
                      <X className="w-3.5 h-3.5" /> Revert to sandbox
                    </button>
                  ) : (
                    <div className="relative inline-block text-left">
                      <button
                        onClick={() => setOpenMenuId(openMenuId === s.id ? null : s.id)}
                        disabled={busyId === s.id}
                        className="inline-flex items-center gap-1.5 h-8 px-3 bg-[#1B1340] text-white text-[11px] uppercase tracking-[0.18em] font-mono rounded-none hover:bg-black disabled:opacity-40"
                        data-testid={`admin-brand-markpaid-${s.id}`}
                      >
                        <Check className="w-3.5 h-3.5" /> Mark paid <ChevronDown className="w-3 h-3" />
                      </button>
                      {openMenuId === s.id && (
                        <div className="absolute right-0 top-full mt-1 w-44 border bg-white shadow-lg z-20" style={{ borderColor: "rgba(27,19,64,0.12)" }} data-testid={`admin-brand-markpaid-menu-${s.id}`}>
                          <button
                            onClick={() => patch(s.id, { is_paid: true, plan: "monthly_10" })}
                            className="w-full text-left px-3 py-2 text-xs hover:bg-[#FFF6EB]"
                            data-testid={`admin-brand-markpaid-monthly-${s.id}`}
                          >Monthly · €10/mo</button>
                          <button
                            onClick={() => patch(s.id, { is_paid: true, plan: "annual_100" })}
                            className="w-full text-left px-3 py-2 text-xs hover:bg-[#FFF6EB]"
                            data-testid={`admin-brand-markpaid-annual-${s.id}`}
                          >Annual · €100/yr</button>
                          <button
                            onClick={() => patch(s.id, { is_paid: true, plan: "comp" })}
                            className="w-full text-left px-3 py-2 text-xs hover:bg-[#FFF6EB] border-t"
                            style={{ borderColor: "rgba(27,19,64,0.06)" }}
                            data-testid={`admin-brand-markpaid-comp-${s.id}`}
                          >Complimentary (no charge)</button>
                        </div>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let on = true;
    api.get("/admin/overview")
      .then((r) => { if (on) setData(r.data); })
      .catch((e) => { if (on) setErr(e?.response?.data?.detail || e.message); })
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, []);

  // Auth + admin gating
  if (user === null) return null; // loading
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;

  const k = data?.kpis || {};

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(180deg,#FFF6EB 0%, #FFEFDB 100%)", color: "#1B1340" }}>
      {/* Header */}
      <header className="border-b" style={{ borderColor: "rgba(27,19,64,0.08)", background: "rgba(255,246,235,0.85)", backdropFilter: "blur(8px)" }}>
        <div className="max-w-7xl mx-auto px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#5E6660] hover:text-[#1B1340]">← Dashboard</Link>
            <span className="font-display font-bold text-lg">Getcurate — Founder</span>
            <span className="px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase bg-[#FF4D6D] text-white">Admin</span>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-[#5E6660]">{user.email}</span>
            <button onClick={logout} className="inline-flex items-center gap-1.5 text-[#5E6660] hover:text-[#1B1340]" data-testid="admin-logout">
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-8 py-10">
        <h1 className="font-display font-medium text-3xl tracking-tight">Overview</h1>
        <p className="mt-1 text-sm text-[#5E6660]">Brand signups, kiosk activity and inbound leads. Not visible to brand users.</p>

        {loading && <div className="mt-10 text-sm text-[#5E6660]">Loading…</div>}
        {err && <div className="mt-10 text-sm text-[#FF4D6D]">{err}</div>}

        {data && (
          <>
            {/* KPI grid */}
            <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <Stat icon={Users}      label="Total brands"        value={k.total_brands}        sub={`${k.signups_last_7d} new in 7d · ${k.signups_last_30d} in 30d`} color="#FF4D6D" />
              <Stat icon={BadgeCheck} label="Paid brands"         value={k.paid_brands}         sub="Active plan members" color="#14F195" />
              <Stat icon={Inbox}      label="Leads (total)"       value={k.leads_total}         sub={`${k.leads_last_7d} new in 7d · ${k.leads_last_30d} in 30d`} color="#FFC233" />
              <Stat icon={Tv2}        label="Experiences"         value={k.total_experiences}   sub={`${k.published_experiences} published`} color="#22C5FF" />
              <Stat icon={ShoppingBag}label="Catalog products"    value={k.total_products}      sub="Across all brands" color="#8B5CF6" />
              <Stat icon={Activity}   label="Kiosk sessions"      value={k.total_kiosk_sessions} sub={`${k.sessions_last_7d} in the last 7d`} color="#FF4D6D" />
              <Stat icon={Activity}   label="Questions written"   value={k.total_questions}     sub="By all brands" color="#FFC233" />
              <Stat icon={Activity}   label="Conversion (rough)"  value={k.total_brands ? Math.round((k.paid_brands / k.total_brands) * 100) + "%" : "—"} sub="paid_brands / total_brands" color="#14F195" />
            </div>

            {/* Brand management (search + mark paid / revert) */}
            <BrandsPanel />

            {/* Recent leads */}
            <section className="mt-12">
              <h2 className="font-display font-medium text-xl">Recent leads</h2>
              <p className="text-sm text-[#5E6660] mt-1">Inbound requests from the &quot;Book demo&quot; form.</p>
              <div className="mt-4 border bg-white rounded-none overflow-hidden" style={{ borderColor: "rgba(27,19,64,0.08)" }}>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[#5E6660] font-mono text-[10px] uppercase tracking-[0.18em] border-b" style={{ borderColor: "rgba(27,19,64,0.08)" }}>
                      <th className="py-3 px-4">Name</th>
                      <th className="py-3 px-4">Email</th>
                      <th className="py-3 px-4">Brand</th>
                      <th className="py-3 px-4">Plan</th>
                      <th className="py-3 px-4">Message</th>
                      <th className="py-3 px-4">When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_leads.length === 0 && (
                      <tr><td className="py-6 px-4 text-[#5E6660]" colSpan={6}>No leads yet.</td></tr>
                    )}
                    {data.recent_leads.map((l) => (
                      <tr key={l.id} className="border-b last:border-b-0" style={{ borderColor: "rgba(27,19,64,0.06)" }}>
                        <td className="py-3 px-4 font-medium">{l.name}</td>
                        <td className="py-3 px-4">
                          <a href={`mailto:${l.email}`} className="text-[#FF4D6D] hover:underline">{l.email}</a>
                        </td>
                        <td className="py-3 px-4 text-[#3F2D5C]">{l.company || "—"}</td>
                        <td className="py-3 px-4 text-[#5E6660]">
                          {l.plan_interest === "monthly_10" ? "€10/mo" : l.plan_interest === "annual_100" ? "€100/yr" : "undecided"}
                        </td>
                        <td className="py-3 px-4 text-[#3F2D5C] max-w-[280px] truncate" title={l.message || ""}>{l.message || "—"}</td>
                        <td className="py-3 px-4 text-[#5E6660] whitespace-nowrap">{fmtDate(l.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
